import { ReactNode, createContext, useContext, useEffect, useState } from 'react';

import TicketList from '../../components/TicketList';
import TicketListGroups from '../../components/TicketListGroups';
import useIsMobile from '../useIsMobile';
import { useEventContext } from './event.context';

export type UiProps = {
  executed: boolean;
  setExecuted: (value: boolean) => void;
  isMobile: boolean;
  handleChangeListView: (componentView: ListView, sessionId?: number) => void;
  handleRenderListView: () => ReactNode;
  listView: ListView;
};

export enum ListView {
  SESSIONS = 'sessions',
  TICKETS = 'tickets',
  TICKETS_GROUPS = 'ticketsGroups'
}

const UiContext = createContext({} as UiProps);

function UiProvider({ children }: { children: ReactNode }) {
  const { startImage, loading, isFootball, sessions } = useEventContext();

  const isMobile = useIsMobile();
  const [executed, setExecuted] = useState(false);
  const [listView, setListView] = useState<ListView>(ListView.SESSIONS);
  const { selectIdDateCarousel } = useEventContext();

  const handleChangeListView = (componentView = ListView.SESSIONS, sessionId?: number) => {
    setListView(componentView);

    if (sessionId) selectIdDateCarousel(sessionId);
  };

  const handleRenderListView = () => {
    if (listView === ListView.TICKETS_GROUPS && startImage) return <TicketList />;
    if (listView === ListView.TICKETS_GROUPS) return <TicketListGroups />;
    if (listView === ListView.TICKETS) return <TicketList />;
    return <TicketList />;
  };

  const createListView = () => {
    if (!loading) {
      const startWithTicketsGroup = startImage;
      const hasAUniqueSession = isFootball || sessions.length <= 1;

      let startedView = startWithTicketsGroup || hasAUniqueSession ? ListView.TICKETS : ListView.SESSIONS;

      if (!isMobile) {
        startedView = startWithTicketsGroup ? ListView.TICKETS_GROUPS : ListView.TICKETS;
      }

      setListView(startedView);
    }
  };

  useEffect(() => {
    createListView();
  }, [isMobile, loading]);

  const shared = {
    executed,
    setExecuted,
    isMobile,
    handleChangeListView,
    handleRenderListView,
    listView
  };

  return (
    <UiContext.Provider value={shared}>
      {children}
    </UiContext.Provider>
  );
}

UiProvider.displayName = 'UiProvider';
export const useUiContext = (): UiProps => useContext(UiContext);

export { UiContext, UiProvider };
